// Internal Route state -----------------------------------------------------------------

type Route = {
  name: string;
  route: string;
};

// External Route state -----------------------------------------------------------------

export enum RouteFields {
  About = 'about',
  AuthReset = 'auth-reset',
  Authorisations = 'authorisations',
  Companies = 'companies',
  Countries = 'countries',
  Dashboard = 'dashboard',
  Forgot = 'forgot',
  Home = 'home',
  Maps = 'maps',
  Multifactor = 'multifactor',
  Placeholder = 'placeholder',
  Products = 'products',
  Profile = 'profile',
  SignIn = 'signin',
  SignInTotp = 'signin-totp',
  SignUp = 'signup',
  States = 'states',
  TimeZones = 'time-zones',
  Waiting = 'waiting',
  AppGroups = 'app-groups',
  AppModuleGroups = 'app-module-groups',
  AppModuleGroupPages = 'app-module-group-pages',
  AppModuleRestrictions = 'app-module-restrictions',
  AppModules = 'app-modules',
  CompanyAccounts = 'company-accounts',
  CompanyConfig = 'company-config',
  CompanyReferences = 'company-references',
  CompanyStatuses = 'company-statuses',
  Databases = 'databases',
  EndpointRouteTypes = 'endpoint-route-types',
  EndpointRoutes = 'endpoint-routes',
  Gateways = 'gateways',
  ProviderAccounts = 'provider-accounts',
  ProviderCompanies = 'provider-companies',
  ProviderStatuses = 'provider-statuses',
  Providers = 'providers',
  ServerTypes = 'server-types',
  Servers = 'servers',
  TwoFactorManagement = 'two-factor-management',
}

export enum RouteApis {
  'oauth' = '/oauth',
  'recovery' = '/recovery',
  'signup' = '/signup',
}

// Declare the route exported const, enum and interface values.

export const RouteNames: Record<string, Route> = {
  [RouteFields.About]: { name: 'About', route: '/about' },
  [RouteFields.Authorisations]: { name: 'Authorisations', route: '/authorisations' },
  [RouteFields.Companies]: { name: 'Companies', route: '/companies' },
  [RouteFields.Countries]: { name: 'Countries', route: '/countries' },
  [RouteFields.Dashboard]: { name: 'Dashboard', route: '/dashboard' },
  [RouteFields.Forgot]: { name: 'Forgot', route: '/forgot' },
  [RouteFields.Home]: { name: 'Home', route: '/' },
  [RouteFields.Maps]: { name: 'Maps', route: '/maps' },
  [RouteFields.Multifactor]: { name: 'Multifactor', route: '/multifactor' },
  [RouteFields.Placeholder]: { name: 'Placeholder', route: '#' },
  [RouteFields.Products]: { name: 'Products', route: '/products' },
  [RouteFields.Profile]: { name: 'Profile', route: '/profile' },
  [RouteFields.SignInTotp]: { name: 'Sign In Totp', route: '/signin-totp' },
  [RouteFields.SignIn]: { name: 'Sign In', route: '/signin' },
  [RouteFields.SignUp]: { name: 'Sign Up', route: '/signup' },
  [RouteFields.States]: { name: 'States', route: '/states' },
  [RouteFields.TimeZones]: { name: 'Time Zones', route: '/time-zones' },
  [RouteFields.Waiting]: { name: 'Waiting', route: '/waiting' },
  [RouteFields.AppGroups]: {
    name: 'App Groups',
    route: '/app-groups',
  },
  [RouteFields.AppModuleGroups]: {
    name: 'App Module Groups',
    route: '/app-module-groups',
  },
  [RouteFields.AppModuleGroupPages]: {
    name: 'App Module Group Pages',
    route: '/app-module-group-pages',
  },
  [RouteFields.AppModuleRestrictions]: {
    name: 'Module Restrictions',
    route: '/app-module-restrictions',
  },
  [RouteFields.AppModules]: {
    name: 'App Modules',
    route: '/app-modules',
  },
  [RouteFields.CompanyAccounts]: {
    name: 'Company Accounts',
    route: '/company-accounts',
  },
  [RouteFields.CompanyConfig]: {
    name: 'Company Config',
    route: '/company-config',
  },
  [RouteFields.CompanyReferences]: {
    name: 'Company References',
    route: '/company-references',
  },
  [RouteFields.CompanyStatuses]: {
    name: 'Company Statuses',
    route: '/company-statuses',
  },
  [RouteFields.Databases]: { name: 'Databases', route: '/databases' },
  [RouteFields.EndpointRoutes]: {
    name: 'Endpoint Routes',
    route: '/endpoint-routes',
  },
  [RouteFields.EndpointRouteTypes]: {
    name: 'Endpoint Route Types',
    route: '/endpoint-route-types',
  },
  [RouteFields.Gateways]: { name: 'Gateways', route: '/gateways' },
  [RouteFields.ProviderAccounts]: {
    name: 'Provider Accounts',
    route: '/provider-accounts',
  },
  [RouteFields.ProviderCompanies]: {
    name: 'Provider/Companies',
    route: '/provider-companies',
  },
  [RouteFields.ProviderStatuses]: {
    name: 'Provider Statuses',
    route: '/provider-statuses',
  },
  [RouteFields.Providers]: { name: 'Providers', route: '/providers' },
  [RouteFields.ServerTypes]: { name: 'Server Types', route: '/server-types' },
  [RouteFields.Servers]: { name: 'Servers', route: '/servers' },
  [RouteFields.TwoFactorManagement]: {
    name: '2FA Management',
    route: '/two-factor-management',
  },
};

export const RouteNamesPrivate: string[] = [
  RouteNames[RouteFields.About].route,
  RouteNames[RouteFields.Authorisations].route,
  RouteNames[RouteFields.Companies].route,
  RouteNames[RouteFields.Countries].route,
  RouteNames[RouteFields.Dashboard].route,
  RouteNames[RouteFields.Maps].route,
  RouteNames[RouteFields.Multifactor].route,
  RouteNames[RouteFields.Products].route,
  RouteNames[RouteFields.Profile].route,
  RouteNames[RouteFields.States].route,
  RouteNames[RouteFields.TimeZones].route,
  RouteNames[RouteFields.AppGroups].route,
  RouteNames[RouteFields.AppModuleGroups].route,
  RouteNames[RouteFields.AppModuleGroupPages].route,
  RouteNames[RouteFields.AppModuleRestrictions].route,
  RouteNames[RouteFields.AppModules].route,
  RouteNames[RouteFields.CompanyAccounts].route,
  RouteNames[RouteFields.CompanyConfig].route,
  RouteNames[RouteFields.CompanyReferences].route,
  RouteNames[RouteFields.CompanyStatuses].route,
  RouteNames[RouteFields.Databases].route,
  RouteNames[RouteFields.EndpointRoutes].route,
  RouteNames[RouteFields.EndpointRouteTypes].route,
  RouteNames[RouteFields.Gateways].route,
  RouteNames[RouteFields.ProviderAccounts].route,
  RouteNames[RouteFields.ProviderCompanies].route,
  RouteNames[RouteFields.ProviderStatuses].route,
  RouteNames[RouteFields.Providers].route,
  RouteNames[RouteFields.ServerTypes].route,
  RouteNames[RouteFields.Servers].route,
  RouteNames[RouteFields.TwoFactorManagement].route,
];

export const RouteNamesPublic: string[] = [RouteNames[RouteFields.Forgot].route, RouteNames[RouteFields.SignIn].route, RouteNames[RouteFields.SignUp].route, RouteNames[RouteFields.Waiting].route];

export const RouteNamesProtected: string[] = [...RouteNamesPrivate, ...RouteNamesPublic];
